import React, { useState, useEffect } from "react";
import { fetchData } from "../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";
import NoData, { hasData } from "../../common/NoData";
import DashboardBarChart from "../../common/DashboardBarChart";
import { POVERTY_COLORS } from "../../../utils/chartTheme";

const formatMpiScore = (value) => (Number(value) || 0).toFixed(2);

const surveyTypeExtraFilter = [
  {
    key: "survey_type",
    label: "Survey Type",
    options: [
      { value: "Baseline", label: "Baseline" },
      { value: "Midline", label: "Midline" },
      { value: "Endline", label: "Endline" },
    ],
  },
];

/* ─── Animated SVG Ring for metric cards */
const RingGauge = ({ pct = 0, color = "#137e81", size = 110, stroke = 9 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(pct, 100) / 100) * circ;
  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <svg
        width={size}
        height={size}
        style={{ transform: "rotate(-90deg)", display: "block" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(0,0,0,0.07)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{
            transition: "stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)",
          }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
        }}
      >
        {(() => {
          const charCount = String(pct).length;
          const scale = Math.min(0.22, 1.1 / charCount);
          const numSize = size * scale;
          const pctSize = numSize * 0.65;
          return (
            <span
              style={{
                color,
                fontWeight: 900,
                lineHeight: 1,
                display: "flex",
                alignItems: "baseline",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ fontSize: numSize }}>{pct}</span>
              <span style={{ fontSize: pctSize }}>%</span>
            </span>
          );
        })()}
      </div>
    </div>
  );
};

const MPIDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  /** Permissions */
  const canViewMPI = permissions.includes("dashboard-mpi");

  const [districtWiseData, setDistrictWiseData] = useState([]);
  const [overallStats, setOverallStats] = useState({
    total_households: 0,
    poor_count: 0,
    poor_percentage: 0,
    not_poor_count: 0,
    not_poor_percentage: 0,
    extremely_poor_count: 0,
    extremely_poor_percentage: 0,
    poor_count_threshold: 0,
    poor_threshold_percentage: 0,
    vulnerable_count: 0,
    vulnerable_percentage: 0,
    not_poor_threshold_count: 0,
    not_poor_threshold_percentage: 0,
    headcount_ratio_h: 0,
    headcount_ratio_h_pct: 0,
    intensity_a: 0,
    intensity_a_pct: 0,
    mpi_m0: 0,
    mpi_m0_pct: 0,
  });

  const [hhThresholdData, setHhThresholdData] = useState({
    overall: null,
    by_district: [],
    by_threshold: null,
    by_district_threshold: [],
  });
  const [individualsData, setIndividualsData] = useState({
    overall: null,
    by_district: [],
  });
  const [indicatorData, setIndicatorData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    survey_type: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (appliedFilters = filters) => {
    setLoading(true);
    try {
      const [
        districtRes,
        overallRes,
        hhThresholdRes,
        individualsRes,
        indicatorRes,
      ] = await Promise.all([
        fetchData(
          "mpi-dashboard/district-wise",
          "GET",
          null,
          {},
          appliedFilters,
          navigate,
          "/mpi-dashboard/district-wise",
          dispatch,
        ),
        fetchData(
          "mpi-dashboard/overall-stats",
          "GET",
          null,
          {},
          appliedFilters,
          navigate,
          "/mpi-dashboard/overall-stats",
          dispatch,
        ),
        fetchData(
          "mpi-dashboard/hh-threshold",
          "GET",
          null,
          {},
          appliedFilters,
          navigate,
          "/mpi-dashboard/hh-threshold",
          dispatch,
        ),
        fetchData(
          "mpi-dashboard/individuals-poor-vs-not-poor",
          "GET",
          null,
          {},
          appliedFilters,
          navigate,
          "/mpi-dashboard/individuals-poor-vs-not-poor",
          dispatch,
        ),
        fetchData(
          "mpi-dashboard/indicator-detail",
          "GET",
          null,
          {},
          appliedFilters,
          navigate,
          "/mpi-dashboard/indicator-detail",
          dispatch,
        ),
      ]);

      setDistrictWiseData(districtRes?.data || []);
      setOverallStats(overallRes?.data || {});
      setHhThresholdData(
        hhThresholdRes?.data || {
          overall: null,
          by_district: [],
          by_threshold: null,
          by_district_threshold: [],
        },
      );
      setIndividualsData(
        individualsRes?.data || { overall: null, by_district: [] },
      );
      setIndicatorData(indicatorRes?.data || []);
    } catch (error) {
      console.error("Error loading MPI dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    loadData(appliedFilters);
  };

  const resetFilters = () => {
    const emptyFilters = {
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      survey_type: "",
    };
    setFilters(emptyFilters);
    setTimeout(() => loadData(emptyFilters), 100);
  };

  const calculateTotals = (data) => {
    return data.reduce(
      (acc, row) => ({
        total_households: acc.total_households + row.total_households,
        poor_count: acc.poor_count + row.poor_count,
        not_poor_count: acc.not_poor_count + row.not_poor_count,
        extremely_poor_count:
          acc.extremely_poor_count + row.extremely_poor_count,
        poor_count_threshold:
          acc.poor_count_threshold + row.poor_count_threshold,
        vulnerable_count: acc.vulnerable_count + row.vulnerable_count,
        not_poor_threshold_count:
          acc.not_poor_threshold_count + row.not_poor_threshold_count,
      }),
      {
        total_households: 0,
        poor_count: 0,
        not_poor_count: 0,
        extremely_poor_count: 0,
        poor_count_threshold: 0,
        vulnerable_count: 0,
        not_poor_threshold_count: 0,
      },
    );
  };

  const totals = calculateTotals(districtWiseData);
  const totalHH = overallStats.total_households || 0;
  const poorHH = overallStats.poor_count || 0;
  const headcountRatioH_pct = overallStats.headcount_ratio_h_pct || 0;
  const intensityA_pct = overallStats.intensity_a_pct || 0;
  const mpiM0 = overallStats.mpi_m0 || 0;
  const mpiM0_pct = overallStats.mpi_m0_pct || 0;
  const mpiScalePercent = mpiM0_pct;

  /* ── Chart configs  */
  const pieChartOptions = {
    chart: { type: "pie", height: 380 },
    plotOptions: { pie: { dataLabels: { minAngleToShowLabel: 18 } } },
    labels: ["MPI Poor", "MPI Not Poor"],
    colors: [POVERTY_COLORS.poor, POVERTY_COLORS.notPoor],
    legend: { position: "bottom", horizontalAlign: "center", fontSize: "14px" },
    dataLabels: {
      enabled: true,
      style: { fontSize: "13px", fontWeight: "bold" },
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    tooltip: {
      y: { formatter: (val) => `${val.toLocaleString()} households` },
    },
  };
  const pieSeries = [
    overallStats.poor_count || 0,
    overallStats.not_poor_count || 0,
  ];

  const thresholdSeries = [
    {
      name: "Extremely Poor (≥0.50)",
      color: POVERTY_COLORS.extremelyPoor,
      data: districtWiseData.map((d) => d.extremely_poor_count),
    },
    {
      name: "Poor (0.33–0.49)",
      color: POVERTY_COLORS.poor,
      data: districtWiseData.map((d) => d.poor_count_threshold),
    },
    {
      name: "Vulnerable (0.20–0.32)",
      color: POVERTY_COLORS.vulnerable,
      data: districtWiseData.map((d) => d.vulnerable_count),
    },
    {
      name: "Not Poor (<0.20)",
      color: POVERTY_COLORS.notPoor,
      data: districtWiseData.map((d) => d.not_poor_threshold_count),
    },
  ];

  const dimColors = {
    Education: {
      text: "text-[#0B5557]",
      bg: "bg-[#E6F2F2]",
      border: "#137E81",
    },
    Health: {
      text: "text-[#8C6A12]",
      bg: "bg-[#FBF6E6]",
      border: "#CBA51F",
    },
    "Living Standards": {
      text: "text-[#34495E]",
      bg: "bg-[#EEF2F6]",
      border: "#4B6584",
    },
  };

  const buildIndicatorRows = () => {
    if (!indicatorData.length) return null;

    const dimRowCount = {};
    indicatorData.forEach((row) => {
      dimRowCount[row.dimension] = (dimRowCount[row.dimension] || 0) + 1;
    });

    const grandTotal = indicatorData.reduce(
      (acc, row) => ({
        dim_pct: acc.dim_pct + row.dimension_pct,
      }),
      { dim_pct: 0 },
    );

    let lastDimension = null;
    const rows = indicatorData.map((row, index) => {
      const isNewDim = row.dimension !== lastDimension;
      if (isNewDim) lastDimension = row.dimension;
      const colors = dimColors[row.dimension] || {
        text: "text-teal-700",
        bg: "bg-teal-50",
        border: "#0f766e",
      };
      const rowBg = index % 2 === 0 ? "#F6F7F9" : "#FFFFFF";

      return (
        <tr
          key={index}
          style={{ backgroundColor: rowBg }}
          className="hover:bg-gray-100 transition-colors"
        >
          {isNewDim ? (
            <td
              rowSpan={dimRowCount[row.dimension]}
              className={`px-4 py-3 border-b font-bold text-center align-middle ${colors.text} ${colors.bg}`}
              style={{
                borderRight: `2px solid ${colors.border}33`,
                minWidth: 130,
              }}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-sm">{row.dimension}</span>
                <span
                  className="text-xs font-normal"
                  style={{ color: colors.border }}
                >
                  Avg: {row.dimension_avg_pct}%
                </span>
              </div>
            </td>
          ) : null}
          <td className="px-4 py-3 border-b text-gray-800 font-medium">
            {row.indicator}
          </td>
          <td className="px-4 py-3 border-b text-center font-semibold text-gray-700">
            {row.weight}%
          </td>
          <td className="px-4 py-3 border-b text-center font-semibold text-gray-800">
            {row.deprived_count.toLocaleString()}
          </td>
          <td className="px-4 py-3 border-b text-center font-semibold text-gray-800">
            {row.pct_hh_deprived}%
          </td>
          <td className="px-4 py-3 border-b text-center font-semibold text-[#137e81]">
            {row.dimension_pct}%
          </td>
        </tr>
      );
    });

    rows.push(
      <tr key="grand-total" className="bg-[#137e81] text-white font-bold">
        <td className="px-4 py-3 text-left" colSpan={2}>
          TOTAL / OVERALL
        </td>
        <td className="px-4 py-3 text-center">100%</td>
        <td className="px-4 py-3 text-center">—</td>
        <td className="px-4 py-3 text-center">—</td>
        <td className="px-4 py-3 text-center">
          {grandTotal.dim_pct.toFixed(4)}%
        </td>
      </tr>,
    );

    return rows;
  };


  const has = {
    headline: hasData(totalHH),
    districts: districtWiseData.length > 0,
    hhIndividuals:
      (hhThresholdData.by_district || []).length > 0 ||
      (individualsData.by_district || []).length > 0,
    indicators: indicatorData.length > 0,
  };

  if (!canViewMPI) {
    return (
      <div className="w-full px-4 bg-gray-50">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <svg
            className="w-12 h-12 text-yellow-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-yellow-700 mb-2 text-balance">
            Access Denied
          </h3>
          <p className="text-yellow-600">
            You don't have permission to view the MPI Dashboard. Please
            contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 bg-gray-50">
      {/* Filters */}
      <div className="w-full flex flex-col justify-between">
        <DashboardSearchFilters
          accordionTitle="Filters"
          defaultExpanded={false}
          fetchOnChange={false}
          apiRoutes={{
            districts: "districts",
            tehsils: "tehsils",
            ucs: "ucs",
            dehs: "dehs",
            villages: "villages",
          }}
          extraFilters={surveyTypeExtraFilter}
          onApply={(appliedFilters) => applyFilters(appliedFilters)}
        />
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
          <p className="mt-2 text-gray-600">Loading MPI data...</p>
        </div>
      )}

      {!loading && <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5 rounded-lg shadow-sm">
        <div className="space-y-4">
          {has.headline && (<>
          {/* ── Poverty Headline ── */}
          <div>
            <h3 className="text-xl font-bold text-[#3A3A3A] my-2 pt-4 text-center uppercase text-balance">
              Poverty Headline (Household Level)
            </h3>
            <p className="text-center text-sm text-gray-500 mb-6">
              Key poverty summary metrics at household level
            </p>
            <div style={{ padding: "8px 0" }}>
              {/* formula legend */}
              <div className="flex items-center justify-center gap-3 mb-8">
                {[
                  { sym: "H", label: "Headcount Ratio", color: "#CBA51F" },
                  { sym: "×", label: null, color: "#6b7280" },
                  { sym: "A", label: "Intensity", color: "#4B6584" },
                  { sym: "=", label: null, color: "#6b7280" },
                  { sym: "M₀", label: "MPI Index", color: "#137e81" },
                ].map((item, i) =>
                  item.label ? (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        background: `${item.color}10`,
                        border: `1px solid ${item.color}33`,
                        borderRadius: "10px",
                        padding: "6px 14px",
                      }}
                    >
                      <span
                        style={{
                          color: item.color,
                          fontWeight: 900,
                          fontSize: "18px",
                          fontFamily: "serif",
                        }}
                      >
                        {item.sym}
                      </span>
                      <span
                        style={{
                          color: item.color + "bb",
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                        }}
                      >
                        {item.label}
                      </span>
                    </div>
                  ) : (
                    <span
                      key={i}
                      style={{
                        color: item.color,
                        fontSize: "22px",
                        fontWeight: 700,
                      }}
                    >
                      {item.sym}
                    </span>
                  ),
                )}
              </div>

              {/* three metric cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr auto 1fr",
                  alignItems: "stretch",
                }}
              >
                <MetricCard
                  accent="#CBA51F"
                  accentDark="#F3E7B8"
                  label="Headcount Ratio"
                  shortLabel="H"
                  value={`${headcountRatioH_pct}%`}
                  pct={headcountRatioH_pct}
                  ringColor="#CBA51F"
                  stats={[
                    { label: "MPI Poor Households", value: poorHH.toLocaleString() },
                    {
                      label: "Total Surveyed",
                      value: totalHH.toLocaleString(),
                    },
                  ]}
                  description="Share of households classified as multidimensionally poor"
                  icon={
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      style={{ width: 22, height: 22 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  }
                />
                <ConnectorArrow symbol="×" />
                <MetricCard
                  accent="#4B6584"
                  accentDark="#DCE3EC"
                  label="Intensity of Poverty"
                  shortLabel="A"
                  value={`${intensityA_pct}%`}
                  pct={intensityA_pct}
                  ringColor="#4B6584"
                  stats={[
                    { label: "Avg. Deprivation", value: "among poor households" },
                    { label: "Poor Household Count", value: poorHH.toLocaleString() },
                  ]}
                  description="Average share of weighted deprivations among MPI poor"
                  icon={
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      style={{ width: 22, height: 22 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  }
                />
                <ConnectorArrow symbol="=" />
                <MetricCard
                  accent="#137e81"
                  accentDark="#ccefef"
                  label="MPI Index"
                  shortLabel="M₀"
                  value={formatMpiScore(mpiM0)}
                  subValue="/ 1.0"
                  pct={mpiScalePercent}
                  ringColor="#137e81"
                  stats={[
                    { label: "MPI Score", value: formatMpiScore(mpiM0) },
                    { label: "Scale", value: "0 → 1 index" },
                  ]}
                  description="Adjusted headcount ratio — the core MPI measure"
                  isResult
                  pillsH={headcountRatioH_pct}
                  pillsA={intensityA_pct}
                  mpiVal={mpiM0}
                  icon={
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      style={{ width: 22, height: 22 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                      />
                    </svg>
                  }
                />
              </div>

              {/* bottom summary strip */}
              <div
                style={{
                  marginTop: "24px",
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "12px",
                }}
              >
                {[
                  {
                    label: "Total Households",
                    value: totalHH.toLocaleString(),
                    color: "#374151",
                  },
                  {
                    label: "MPI Poor Households",
                    value: poorHH.toLocaleString(),
                    color: "#CBA51F",
                  },
                  {
                    label: "MPI Not Poor",
                    value: (overallStats.not_poor_count || 0).toLocaleString(),
                    color: "#137e81",
                  },
                ].map((s, i) => (
                  <div
                    key={i}
                    style={{
                      background: "rgba(19,126,129,0.05)",
                      border: "1px solid rgba(19,126,129,0.15)",
                      borderRadius: "12px",
                      padding: "14px 20px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {s.label}
                    </span>
                    <span
                      style={{
                        color: s.color,
                        fontSize: "15px",
                        fontWeight: 700,
                      }}
                    >
                      {s.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </>)}

          {has.districts && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div>
            <h3 className="text-xl font-bold text-[#3A3A3A] my-2 text-center uppercase text-balance">
              District-Wise MPI Core Indicators
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#137e81] text-white">
                    <th className="px-4 py-3 text-left font-medium">
                      District
                    </th>
                    <th className="px-4 py-3 text-center font-medium">
                      # of Households Surveyed
                    </th>
                    <th className="px-4 py-3 text-center font-medium">
                      Headcount Ratio (H)
                      <br />
                      <span className="text-xs font-normal text-teal-200">
                        % MPI Poor Households
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center font-medium">
                      Intensity (A)
                      <br />
                      <span className="text-xs font-normal text-teal-200">
                        Avg. Deprivation
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center font-medium">
                      MPI Core (M₀ = H×A)
                      <br />
                      <span className="text-xs font-normal text-teal-200">
                        Score (0 – 1)
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {districtWiseData.length > 0 ? (
                    <>
                      {districtWiseData.map((district, index) => (
                        <tr
                          key={index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#F6F7F9" : "#FFFFFF",
                          }}
                          className="hover:bg-gray-100 transition-colors"
                        >
                          <td className="px-4 py-3 text-gray-800 border-b font-medium">
                            {district.district_name}
                          </td>
                          <td className="px-4 py-3 text-center border-b font-semibold">
                            {district.total_households.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center border-b text-gray-800 font-semibold">
                            {district.headcount_ratio_h_pct}%
                          </td>
                          <td className="px-4 py-3 text-center border-b text-gray-800 font-semibold">
                            {district.intensity_a_pct}%
                          </td>
                          <td className="px-4 py-3 text-center border-b text-[#137e81] font-semibold">
                            {formatMpiScore(district.mpi_m0)}
                          </td>
                        </tr>
                      ))}
                      {(() => {
                        const totalHHAll = districtWiseData.reduce(
                          (s, d) => s + d.total_households,
                          0,
                        );
                        const totalPoorAll = districtWiseData.reduce(
                          (s, d) => s + d.poor_count,
                          0,
                        );
                        const avgH =
                          totalHHAll > 0
                            ? parseFloat(
                                ((totalPoorAll / totalHHAll) * 100).toFixed(2),
                              )
                            : 0;
                        const avgA =
                          districtWiseData.length > 0
                            ? parseFloat(
                                (
                                  districtWiseData.reduce(
                                    (s, d) => s + d.intensity_a_pct,
                                    0,
                                  ) / districtWiseData.length
                                ).toFixed(2),
                              )
                            : 0;
                        const avgM0 =
                          districtWiseData.length > 0
                            ? districtWiseData.reduce(
                                (s, d) => s + (Number(d.mpi_m0) || 0),
                                0,
                              ) / districtWiseData.length
                            : 0;
                        return (
                          <tr className="bg-[#137e81] text-white font-bold">
                            <td className="px-4 py-3 text-left">TOTAL / AVG</td>
                            <td className="px-4 py-3 text-center">
                              {totalHHAll.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-center">{avgH}%</td>
                            <td className="px-4 py-3 text-center">{avgA}%</td>
                            <td className="px-4 py-3 text-center">{formatMpiScore(avgM0)}</td>
                          </tr>
                        );
                      })()}
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No MPI data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>)}

          {has.districts && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div>
            <h3 className="text-xl font-bold text-[#3A3A3A] my-2 text-center uppercase text-balance">
              MPI Poverty Distribution
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#137e81] text-white">
                    <th className="px-4 py-3 text-left font-medium">
                      District
                    </th>
                    <th className="px-4 py-3 text-center font-medium">
                      # of Households
                    </th>
                    <th
                      className="px-4 py-3 text-center font-medium"
                      colSpan="2"
                    >
                      MPI Poor
                    </th>
                    <th
                      className="px-4 py-3 text-center font-medium"
                      colSpan="2"
                    >
                      MPI Not Poor
                    </th>
                  </tr>
                  <tr className="bg-[#137e81] text-white">
                    <th></th>
                    <th></th>
                    <th className="px-4 py-2 text-center text-sm font-medium">
                      #
                    </th>
                    <th className="px-4 py-2 text-center text-sm font-medium">
                      %
                    </th>
                    <th className="px-4 py-2 text-center text-sm font-medium">
                      #
                    </th>
                    <th className="px-4 py-2 text-center text-sm font-medium">
                      %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {districtWiseData.length > 0 ? (
                    <>
                      {districtWiseData.map((district, index) => (
                        <tr
                          key={index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#F6F7F9" : "#FFFFFF",
                          }}
                          className="hover:bg-gray-100 transition-colors"
                        >
                          <td className="px-4 py-3 text-gray-800 border-b font-medium">
                            {district.district_name}
                          </td>
                          <td className="px-4 py-3 text-center border-b font-semibold">
                            {district.total_households.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center border-b text-gray-800 font-semibold">
                            {district.poor_count.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center border-b text-gray-800 font-semibold">
                            {district.poor_percentage}%
                          </td>
                          <td className="px-4 py-3 text-center border-b text-gray-800 font-semibold">
                            {district.not_poor_count.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center border-b text-gray-800 font-semibold">
                            {district.not_poor_percentage}%
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-[#137e81] text-white font-bold">
                        <td className="px-4 py-3 text-left">TOTAL</td>
                        <td className="px-4 py-3 text-center">
                          {totals.total_households.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {totals.poor_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {totals.total_households > 0
                            ? (
                                (totals.poor_count / totals.total_households) *
                                100
                              ).toFixed(2)
                            : 0}
                          %
                        </td>
                        <td className="px-4 py-3 text-center">
                          {totals.not_poor_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {totals.total_households > 0
                            ? (
                                (totals.not_poor_count /
                                  totals.total_households) *
                                100
                              ).toFixed(2)
                            : 0}
                          %
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No MPI data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>)}

          {has.districts && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div>
            <h3 className="text-xl font-bold text-[#3A3A3A] my-2 text-center uppercase text-balance">
              MPI Distribution with Threshold Categories
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#137e81] text-white">
                    <th className="px-3 py-2 text-left font-medium">
                      District
                    </th>
                    <th className="px-3 py-2 text-center font-medium">
                      # of Households
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium"
                      colSpan="2"
                    >
                      Extremely Poor
                      <br />
                      (≥0.50)
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium"
                      colSpan="2"
                    >
                      Poor
                      <br />
                      (0.33–0.49)
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium"
                      colSpan="2"
                    >
                      Vulnerable
                      <br />
                      (0.20–0.32)
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium"
                      colSpan="2"
                    >
                      Not Poor
                      <br />
                      (&lt;0.20)
                    </th>
                  </tr>
                  <tr className="bg-[#137e81] text-white">
                    <th></th>
                    <th></th>
                    <th className="px-2 py-1 text-center text-xs">#</th>
                    <th className="px-2 py-1 text-center text-xs">%</th>
                    <th className="px-2 py-1 text-center text-xs">#</th>
                    <th className="px-2 py-1 text-center text-xs">%</th>
                    <th className="px-2 py-1 text-center text-xs">#</th>
                    <th className="px-2 py-1 text-center text-xs">%</th>
                    <th className="px-2 py-1 text-center text-xs">#</th>
                    <th className="px-2 py-1 text-center text-xs">%</th>
                  </tr>
                </thead>
                <tbody>
                  {districtWiseData.length > 0 ? (
                    <>
                      {districtWiseData.map((district, index) => (
                        <tr
                          key={index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#F6F7F9" : "#FFFFFF",
                          }}
                          className="hover:bg-gray-100 transition-colors"
                        >
                          <td className="px-3 py-2 text-gray-800 border-b font-medium">
                            {district.district_name}
                          </td>
                          <td className="px-3 py-2 text-center border-b font-semibold">
                            {district.total_households.toLocaleString()}
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.extremely_poor_count.toLocaleString()}
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.extremely_poor_percentage}%
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.poor_count_threshold.toLocaleString()}
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.poor_threshold_percentage}%
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.vulnerable_count.toLocaleString()}
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.vulnerable_percentage}%
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.not_poor_threshold_count.toLocaleString()}
                          </td>
                          <td className="px-2 py-2 text-center border-b text-gray-800 font-semibold">
                            {district.not_poor_threshold_percentage}%
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-[#137e81] text-white font-bold">
                        <td className="px-3 py-2 text-left">TOTAL</td>
                        <td className="px-3 py-2 text-center">
                          {totals.total_households.toLocaleString()}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.extremely_poor_count.toLocaleString()}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.total_households > 0
                            ? (
                                (totals.extremely_poor_count /
                                  totals.total_households) *
                                100
                              ).toFixed(2)
                            : 0}
                          %
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.poor_count_threshold.toLocaleString()}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.total_households > 0
                            ? (
                                (totals.poor_count_threshold /
                                  totals.total_households) *
                                100
                              ).toFixed(2)
                            : 0}
                          %
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.vulnerable_count.toLocaleString()}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.total_households > 0
                            ? (
                                (totals.vulnerable_count /
                                  totals.total_households) *
                                100
                              ).toFixed(2)
                            : 0}
                          %
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.not_poor_threshold_count.toLocaleString()}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {totals.total_households > 0
                            ? (
                                (totals.not_poor_threshold_count /
                                  totals.total_households) *
                                100
                              ).toFixed(2)
                            : 0}
                          %
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan="10"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No MPI data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>)}

          {has.hhIndividuals && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div>
            <div className="grid grid-cols-2 gap-0 mb-1">
              <h4
                className="text-sm font-bold text-[#3A3A3A] text-center uppercase py-1"
                style={{ borderBottom: "2px solid #137e81" }}
              >
                # and % of Households Overall, by District and by Threshold
                Categorization
              </h4>
              <h4
                className="text-sm font-bold text-[#3A3A3A] text-center uppercase py-1"
                style={{ borderBottom: "2px solid #137e81" }}
              >
                # and % of Individuals in MPI Poor VS Not Poor Households
              </h4>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#137e81] text-white">
                    <th
                      className="px-3 py-2 text-center font-medium border border-teal-600"
                      rowSpan={3}
                      style={{ minWidth: 100 }}
                    >
                      District
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium border border-teal-600"
                      rowSpan={3}
                      style={{ minWidth: 80 }}
                    >
                      # of Households
                      <br />
                      Assessed
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium border border-teal-600"
                      colSpan={2}
                    >
                      MPI Poor
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium border border-teal-600"
                      colSpan={2}
                    >
                      MPI Not Poor
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium border border-teal-600"
                      rowSpan={3}
                      style={{ minWidth: 90, borderLeft: "3px solid #0e6467" }}
                    >
                      # of
                      <br />
                      Individuals
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium border border-teal-600"
                      colSpan={2}
                    >
                      In MPI Poor Households
                    </th>
                    <th
                      className="px-3 py-2 text-center font-medium border border-teal-600"
                      colSpan={2}
                    >
                      In MPI Not Poor Households
                    </th>
                  </tr>
                  <tr className="bg-[#137e81] text-white">
                    <th
                      className="px-3 py-1 text-center font-medium border border-teal-600 text-xs"
                      colSpan={2}
                    >
                      Score ≥ 0.33
                    </th>
                    <th
                      className="px-3 py-1 text-center font-medium border border-teal-600 text-xs"
                      colSpan={2}
                    >
                      Score &lt;0.33
                    </th>
                    <th
                      className="px-3 py-1 text-center font-medium border border-teal-600 text-xs"
                      colSpan={2}
                    >
                      Score ≥ 0.33
                    </th>
                    <th
                      className="px-3 py-1 text-center font-medium border border-teal-600 text-xs"
                      colSpan={2}
                    >
                      Score &lt;0.33
                    </th>
                  </tr>
                  <tr className="bg-[#137e81] text-white">
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      Freq
                    </th>
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      %
                    </th>
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      Freq
                    </th>
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      %
                    </th>
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      Freq
                    </th>
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      %
                    </th>
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      Freq
                    </th>
                    <th className="px-2 py-1 text-center text-xs border border-teal-600">
                      %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const hhMap = new Map(
                      (hhThresholdData.by_district || []).map((d) => [
                        d.district_id,
                        d,
                      ]),
                    );
                    const indMap = new Map(
                      (individualsData.by_district || []).map((d) => [
                        d.district_id,
                        d,
                      ]),
                    );
                    const allIds = Array.from(
                      new Set([...hhMap.keys(), ...indMap.keys()]),
                    );
                    const rows = allIds
                      .map((id) => ({
                        id,
                        name:
                          hhMap.get(id)?.district_name ||
                          indMap.get(id)?.district_name ||
                          "Unknown",
                        hh: hhMap.get(id) || {},
                        ind: indMap.get(id) || {},
                      }))
                      .sort((a, b) => a.name.localeCompare(b.name));

                    if (rows.length === 0) {
                      return (
                        <tr>
                          <td
                            colSpan={11}
                            className="px-4 py-8 text-center text-gray-500"
                          >
                            No data available
                          </td>
                        </tr>
                      );
                    }

                    const tot = rows.reduce(
                      (acc, r) => ({
                        hh_total: acc.hh_total + (r.hh.total_hh_surveyed || 0),
                        hh_poor: acc.hh_poor + (r.hh.mpi_poor_count || 0),
                        hh_not_poor:
                          acc.hh_not_poor + (r.hh.mpi_not_poor_count || 0),
                        ind_total:
                          acc.ind_total + (r.ind.total_individuals || 0),
                        ind_poor: acc.ind_poor + (r.ind.in_poor_hh_count || 0),
                        ind_not_poor:
                          acc.ind_not_poor + (r.ind.in_not_poor_hh_count || 0),
                      }),
                      {
                        hh_total: 0,
                        hh_poor: 0,
                        hh_not_poor: 0,
                        ind_total: 0,
                        ind_poor: 0,
                        ind_not_poor: 0,
                      },
                    );

                    return (
                      <>
                        {rows.map((r, i) => {
                          const hhTotal = r.hh.total_hh_surveyed || 0;
                          const hhPoor = r.hh.mpi_poor_count || 0;
                          const hhNotPoor = r.hh.mpi_not_poor_count || 0;
                          const hhPoorPct =
                            r.hh.mpi_poor_pct !== undefined
                              ? r.hh.mpi_poor_pct
                              : hhTotal > 0
                                ? Math.round((hhPoor / hhTotal) * 100)
                                : 0;
                          const hhNotPoorPct =
                            r.hh.mpi_not_poor_pct !== undefined
                              ? r.hh.mpi_not_poor_pct
                              : hhTotal > 0
                                ? Math.round((hhNotPoor / hhTotal) * 100)
                                : 0;
                          const indTotal = r.ind.total_individuals || 0;
                          const indPoor = r.ind.in_poor_hh_count || 0;
                          const indNotPoor = r.ind.in_not_poor_hh_count || 0;
                          const indPoorPct =
                            r.ind.in_poor_hh_pct !== undefined
                              ? r.ind.in_poor_hh_pct
                              : indTotal > 0
                                ? Math.round((indPoor / indTotal) * 100)
                                : 0;
                          const indNotPoorPct =
                            r.ind.in_not_poor_hh_pct !== undefined
                              ? r.ind.in_not_poor_hh_pct
                              : indTotal > 0
                                ? Math.round((indNotPoor / indTotal) * 100)
                                : 0;

                          return (
                            <tr
                              key={r.id}
                              style={{
                                backgroundColor:
                                  i % 2 === 0 ? "#F6F7F9" : "#FFFFFF",
                              }}
                              className="hover:bg-gray-100 transition-colors"
                            >
                              <td className="px-3 py-2 border border-gray-200 font-medium text-gray-800">
                                {r.name}
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center font-semibold">
                                {hhTotal.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {hhPoor.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {hhPoorPct}%
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {hhNotPoor.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {hhNotPoorPct}%
                              </td>
                              <td
                                className="px-3 py-2 border border-gray-200 text-center font-semibold"
                                style={{ borderLeft: "3px solid #137e81" }}
                              >
                                {indTotal.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {indPoor.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {indPoorPct}%
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {indNotPoor.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 border border-gray-200 text-center text-gray-800 font-semibold">
                                {indNotPoorPct}%
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-[#137e81] text-white font-bold">
                          <td className="px-3 py-2 border border-teal-600">
                            Overall
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.hh_total.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.hh_poor.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.hh_total > 0
                              ? Math.round((tot.hh_poor / tot.hh_total) * 100)
                              : 0}
                            %
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.hh_not_poor.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.hh_total > 0
                              ? Math.round(
                                  (tot.hh_not_poor / tot.hh_total) * 100,
                                )
                              : 0}
                            %
                          </td>
                          <td
                            className="px-3 py-2 border border-teal-600 text-center"
                            style={{ borderLeft: "3px solid #0e6467" }}
                          >
                            {tot.ind_total.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.ind_poor.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.ind_total > 0
                              ? Math.round((tot.ind_poor / tot.ind_total) * 100)
                              : 0}
                            %
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.ind_not_poor.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-teal-600 text-center">
                            {tot.ind_total > 0
                              ? Math.round(
                                  (tot.ind_not_poor / tot.ind_total) * 100,
                                )
                              : 0}
                            %
                          </td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          </>)}

          {has.indicators && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div>
            <h3 className="text-xl font-bold text-[#3A3A3A] my-2 text-center uppercase text-balance">
              MPI Poverty Distribution by Indicators
            </h3>
            <p className="text-center text-sm text-gray-500 mb-4">
              Deprivation breakdown across all 15 indicators grouped by
              dimension
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#137e81] text-white">
                    <th
                      className="px-4 py-3 text-center font-medium"
                      style={{
                        minWidth: 130,
                        borderRight: "2px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      Dimension
                    </th>
                    <th className="px-4 py-3 text-left font-medium">
                      Indicator
                    </th>
                    <th
                      className="px-4 py-3 text-center font-medium"
                      style={{ minWidth: 90 }}
                    >
                      Weight (%)
                    </th>
                    <th
                      className="px-4 py-3 text-center font-medium"
                      style={{ minWidth: 110 }}
                    >
                      # Households Deprived
                    </th>
                    <th
                      className="px-4 py-3 text-center font-medium"
                      style={{ minWidth: 110 }}
                    >
                      % Households Deprived
                    </th>
                    <th
                      className="px-4 py-3 text-center font-medium"
                      style={{ minWidth: 140 }}
                    >
                      Dimension %
                      <br />
                      <span className="text-xs font-normal text-teal-200">
                        (Avg. Contribution)
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {indicatorData.length > 0 ? (
                    buildIndicatorRows()
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No indicator data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>)}

          {(has.headline || has.districts) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-4">
              <h3 className="text-base font-bold text-[#3A3A3A] mb-1 text-center uppercase text-balance">
                MPI Poor vs Not Poor
              </h3>
              <p className="text-center text-xs text-gray-400 mb-3">
                Overall household poverty split
              </p>
              {overallStats.total_households > 0 ? (
                <ReactApexChart
                  options={pieChartOptions}
                  series={pieSeries}
                  type="pie"
                  height={380}
                />
              ) : (
                <NoData />
              )}
            </div>

            <div className="bg-white rounded-lg p-4">
              <h3 className="text-base font-bold text-[#3A3A3A] mb-1 text-center uppercase text-balance">
                MPI Threshold Distribution
              </h3>
              <p className="text-center text-xs text-gray-400 mb-3">
                Poverty category breakdown by district
              </p>
       
              <DashboardBarChart
                vertical
                categories={districtWiseData.map((d) => d.district_name)}
                series={thresholdSeries}
                segmentLabel="count"
                unit="households"
                height={380}
              />
            </div>
          </div>
          </>)}

          {!has.headline && !has.districts && !has.hhIndividuals && !has.indicators && (
            <NoData className="py-16" />
          )}
        </div>
      </div>}

    </div>
  );
};

const ConnectorArrow = ({ symbol }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 4px",
      gap: "6px",
    }}
  >
    <div style={{ width: 1, flex: 1, background: "rgba(19,126,129,0.15)" }} />
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: "rgba(19,126,129,0.08)",
        border: "1px solid rgba(19,126,129,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#137e81",
        fontSize: 18,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {symbol}
    </div>
    <div style={{ width: 1, flex: 1, background: "rgba(19,126,129,0.15)" }} />
  </div>
);

const MetricCard = ({
  accent,
  accentDark,
  label,
  shortLabel,
  value,
  subValue,
  pct,
  ringColor,
  stats,
  description,
  icon,
  isResult,
  pillsH,
  pillsA,
  mpiVal,
}) => (
  <div
    style={{
      background: "#ffffff",
      border: `1px solid ${accent}30`,
      borderRadius: "16px",
      padding: "24px 20px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      position: "relative",
      overflow: "hidden",
      boxShadow: `0 4px 24px ${accent}15`,
    }}
  >
    <div
      style={{
        position: "absolute",
        top: -40,
        right: -40,
        width: 130,
        height: 130,
        borderRadius: "50%",
        background: accent,
        opacity: 0.06,
        filter: "blur(30px)",
        pointerEvents: "none",
      }}
    />
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div>
        <p
          style={{
            color: accent,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          {label}
        </p>
        <p
          style={{
            color: "#1f2937",
            fontSize: 22,
            fontWeight: 900,
            fontFamily: "serif",
          }}
        >
          {shortLabel}
        </p>
      </div>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          background: `${accent}12`,
          border: `1px solid ${accent}25`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accent,
        }}
      >
        {icon}
      </div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
      <RingGauge pct={pct} color={ringColor} size={110} stroke={9} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              color: "#111827",
              fontSize: 32,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            {value}
          </span>
          {subValue && (
            <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>
              {subValue}
            </span>
          )}
        </div>
        <p
          style={{
            color: "#6b7280",
            fontSize: 11,
            marginTop: 6,
            lineHeight: 1.5,
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
        >
          {description}
        </p>
      </div>
    </div>
    {isResult && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            background: "rgba(203,165,31,0.1)",
            border: "1px solid rgba(203,165,31,0.25)",
            color: "#CBA51F",
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 10px",
            borderRadius: 99,
          }}
        >
          H {pillsH}%
        </span>
        <span style={{ color: "#9ca3af", fontSize: 12 }}>×</span>
        <span
          style={{
            background: "rgba(75,101,132,0.1)",
            border: "1px solid rgba(75,101,132,0.25)",
            color: "#4B6584",
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 10px",
            borderRadius: 99,
          }}
        >
          A {pillsA}%
        </span>
        <span style={{ color: "#9ca3af", fontSize: 12 }}>=</span>
        <span
          style={{
            background: "rgba(19,126,129,0.12)",
            border: "1px solid rgba(19,126,129,0.3)",
            color: "#137e81",
            fontSize: 11,
            fontWeight: 800,
            padding: "2px 10px",
            borderRadius: 99,
          }}
        >
          {mpiVal}
        </span>
      </div>
    )}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 8,
        borderTop: `1px solid ${accent}18`,
        paddingTop: 14,
        marginTop: "auto",
      }}
    >
      {stats.map((s, i) => (
        <div key={i} style={{ textAlign: i === 1 ? "right" : "left" }}>
          <p style={{ color: "#9ca3af", fontSize: 10, marginBottom: 2 }}>
            {s.label}
          </p>
          <p style={{ color: "#1f2937", fontSize: 13, fontWeight: 700 }}>
            {s.value}
          </p>
        </div>
      ))}
    </div>
  </div>
);

export default MPIDashboard;
